/**
 * shadcn/ui Table parts on the brand tokens. Author: Mahmoud Ragab Ahmed.
 *
 * DIFFERENCE FROM UPSTREAM, on purpose: shadcn's <Table> wraps itself in an
 * `overflow-x-auto` div. Any overflow other than visible makes that div the nearest
 * scroll container, so a sticky header sticks to IT (which never scrolls vertically) and
 * not to the register viewport. Here the table renders bare; the DataTable owns the one
 * scroll viewport (max-height 70vh, overflow auto, UX_SPEC 2.4).
 */
import { cn } from "@/lib/utils";

export function Table({ className, ...props }: React.ComponentProps<"table">): React.JSX.Element {
  return (
    <table
      data-slot="table"
      className={cn("w-full caption-top border-separate border-spacing-0 text-sm", className)}
      {...props}
    />
  );
}

export function TableHeader({
  className,
  ...props
}: React.ComponentProps<"thead">): React.JSX.Element {
  return <thead data-slot="table-header" className={className} {...props} />;
}

export function TableBody({
  className,
  ...props
}: React.ComponentProps<"tbody">): React.JSX.Element {
  return <tbody data-slot="table-body" className={className} {...props} />;
}

export function TableRow({ className, ...props }: React.ComponentProps<"tr">): React.JSX.Element {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        // Wave Experience A: every register is banded; the hover still lifts a row.
        "transition-colors even:bg-row-band hover:bg-row-hover [&:last-child>td]:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: React.ComponentProps<"th">): React.JSX.Element {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "sticky top-0 z-2 border-b border-rule-strong bg-card px-3 py-2 text-start align-middle font-normal whitespace-nowrap",
        "text-xs tracking-[0.08em] text-muted-foreground uppercase",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: React.ComponentProps<"td">): React.JSX.Element {
  return (
    <td
      data-slot="table-cell"
      className={cn("border-b border-rule px-3 py-2.5 align-top text-foreground", className)}
      {...props}
    />
  );
}

export function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">): React.JSX.Element {
  return <caption data-slot="table-caption" className={cn("sr-only", className)} {...props} />;
}
